import * as React from 'react';
import { useWindowSize } from 'react-use';
import { Box, useTheme } from '@mui/material';

import { EnhancedTable, ZoneCardList } from 'components';
import { convertZonesToTableData, downloadBlob } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { ZoneDeactivateDialog, ZoneServiceItemsDialog, ZoneUpdateDialog, ZoneCopyDialog } from 'dialogs/zone';
import { useActiveZonesTableRows } from './zones.hook';
import { updateLayout } from 'store/slices/layout.slice';
import { SuspendServiceDialog } from 'dialogs/service-interruptions';
import { useOutletContext } from 'react-router-dom';
import { useLazyDownloadDecalQuery } from 'store/apis';
import { useLoadingToast } from 'hooks/toasts/loading-toast.hook';
import { IZone } from 'models';

type ActionRegistry = {
  downloadDecals?: () => void;
};

type ZonesContextType = {
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  registerAction: (name: keyof ActionRegistry, fn: any) => void;
  runAction: (name: keyof ActionRegistry) => void;
  zones: IZone[];
  loading: boolean;
};

type State = {
  zoneId: number | null;
  categoryId: number | null;
  visibleZoneServiceItemsDialog: boolean;
  visibleZoneUpdateDialog: boolean;
  visibleZoneDeactivateDialog: boolean;
  visibleSuspendServiceDialog: boolean;
  visibleZoneCopyDialog: boolean;
  isLoading: boolean;
};

export const ZonesActivePage = () => {
  const dispatch = useAppDispatch();
  const { orgSlug, isAdmin } = useAppSelector(selectCurrentUserInfo);
  const theme = useTheme();
  const { width } = useWindowSize();
  const [downloadDecalTrigger] = useLazyDownloadDecalQuery();
  const [
    {
      zoneId,
      categoryId,
      visibleZoneServiceItemsDialog,
      visibleZoneUpdateDialog,
      visibleZoneDeactivateDialog,
      visibleSuspendServiceDialog,
      visibleZoneCopyDialog,
    },
    setState,
  ] = React.useState<State>({
    zoneId: null,
    categoryId: null,
    visibleZoneServiceItemsDialog: false,
    visibleZoneUpdateDialog: false,
    visibleZoneDeactivateDialog: false,
    visibleSuspendServiceDialog: false,
    visibleZoneCopyDialog: false,
    isLoading: false,
  });

  const { selected, setSelected, registerAction, zones, loading } = useOutletContext<ZonesContextType>();

  const [, columnKeys, columns, rows] = useActiveZonesTableRows({
    setState,
    loading,
    zones,
  });

  const loadingToast = useLoadingToast();

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Zones' }));
  }, []);

  const handleDownloadDecals = React.useCallback(async () => {
    if (!selected.length) {
      return;
    }
    try {
      loadingToast.open({ message: 'Downloading Decal' });
      const zoneMap = new Map(rows.map((z) => [String(z.id), z]));

      await Promise.all(
        selected.map(async (zoneId) => {
          const zone = zoneMap.get(String(zoneId));
          const result = await downloadDecalTrigger({
            orgSlug,
            id: Number(zoneId),
            format: 'pdf',
          });

          if (result.isSuccess && result.data) {
            downloadBlob({
              data: result.data,
              filename: `zone_${zone?.code ?? zoneId}_decal.pdf`,
              type: 'application/pdf',
            });
          }
        }),
      );
    } finally {
      loadingToast.close();
    }
  }, [selected, rows, orgSlug, loadingToast, downloadDecalTrigger]);

  React.useEffect(() => {
    registerAction('downloadDecals', handleDownloadDecals);
  }, [registerAction, handleDownloadDecals]);

  return (
    <>
      {width < theme.breakpoints.values.sm ? (
        <Box sx={{ paddingTop: 2 }}>
          <ZoneCardList
            zones={rows.sort((a, b) => (a.floorInfo > b.floorInfo ? 1 : a.floorInfo < b.floorInfo ? -1 : 0))}
            noDataLabel="No active zone"
          />
        </Box>
      ) : (
        <EnhancedTable
          config={{ toolbar: false, rowCheckable: true }}
          loading={loading}
          loadingLabel={'Loading active zones'}
          noDataLabel={'No active zones'}
          data={rows}
          convertFunctionForDataToTableData={convertZonesToTableData(isAdmin)}
          headColumns={columns}
          columnKeys={columnKeys}
          defaultSortColumnName="floorInfo"
          defaultSortDirection="asc"
          styles={{ tableStyle: { minWidth: 0 } }}
          initialRowsPerPage={10}
          visiblePagination
          rowActions={{}}
          selected={selected}
          setSelected={setSelected}
        />
      )}

      {typeof zoneId === 'number' && (
        <>
          <ZoneServiceItemsDialog
            visible={visibleZoneServiceItemsDialog}
            setVisible={(visibleZoneServiceItemsDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneServiceItemsDialog }))
            }
            zoneId={zoneId}
            categoryId={categoryId}
          />

          <ZoneUpdateDialog
            visible={visibleZoneUpdateDialog}
            setVisible={(visibleZoneUpdateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneUpdateDialog }))
            }
            zoneId={zoneId}
          />

          <ZoneCopyDialog
            visible={visibleZoneCopyDialog}
            setVisible={(visibleZoneCopyDialog: boolean) => setState((prev) => ({ ...prev, visibleZoneCopyDialog }))}
            zoneId={zoneId}
          />

          <ZoneDeactivateDialog
            visible={visibleZoneDeactivateDialog}
            setVisible={(visibleZoneDeactivateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneDeactivateDialog }))
            }
            zoneId={zoneId}
          />

          <SuspendServiceDialog
            visible={visibleSuspendServiceDialog}
            setVisible={(visibleSuspendServiceDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleSuspendServiceDialog }))
            }
            zoneId={zoneId}
          />
        </>
      )}
    </>
  );
};
